import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import Colors from '../../../../../pages/insights/components/graphs/utilities/colors';
import messages from '../messages';
import NoHistoricalData from './no-historical-data';
import style from './velocity-chart.module.less';

const CHART_HEIGHT = 300;
const HISTORICAL_COLOR = Colors.BAR_COLOR_BLUE;
const FORECAST_COLOR = Colors.BAR_COLOR_GREEN;
const STOCK_LEVEL_COLOR = Colors.BAR_COLOR_PURPLE;
const REFERENCE_LINE_COLOR = Colors.GREY_HEX_VALUE;
const CURRENT_DAY_COLOR = Colors.BAR_COLOR_ROSE;
const CURRENT_DAY_SIZE = 8;

/**
 * VelocityChart component that displays historical and forecasted order velocity
 * @param {object} props - Component props
 * @param {Array<{date: string, value: number, forecastValue: number, stockLevel: number, isForecast: boolean}>} [props.data] - The chart data array
 * @param {boolean} [props.isLoading] - Whether the chart data is loading
 * @param {boolean} [props.hasSufficientHistoricalData] - Whether the product has sufficient historical data
 * @returns {React.ReactElement} The rendered VelocityChart component
 */
export const VelocityChart = ({ data, isLoading = false, hasSufficientHistoricalData = true }) => {
  const intl = useIntl();

  const currentDayIndex = useMemo(() => {
    if (!data || data.length === 0) return -1;
    return data.findIndex((item) => item.isCurrentDay);
  }, [data]);

  const formatXAxis = useMemo(() => {
    return (date) => {
      if (!date) return '';
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString(undefined, {
        month: 'short',
        year: '2-digit',
      });
    };
  }, []);

  const CustomTooltip = useMemo(
    () =>
      ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          return (
            <div className={style.tooltip}>
              <p className={style['tooltip-date']}>{formatXAxis(label)}</p>
              {payload.map((entry, index) => (
                <p key={`tooltip-${index}`} className={style['tooltip-value']} style={{ color: entry.color }}>
                  {entry.name}: {Math.round(entry.value)}
                </p>
              ))}
            </div>
          );
        }
        return null;
      },
    [formatXAxis],
  );

  if (isLoading) {
    return (
      <div className={style.loading}>
        <LoadingIndicator className={style['loading-indicator']} theme='grey' />
      </div>
    );
  }

  if (!hasSufficientHistoricalData) {
    return <NoHistoricalData />;
  }

  if (!data || data.length === 0) {
    return <div className={style['no-data']}>{intl.formatMessage(messages.velocityChartNoDataMessage)}</div>;
  }

  return (
    <div className={style['velocity-chart']}>
      <ResponsiveContainer height={CHART_HEIGHT} width='100%'>
        <LineChart
          data={data}
          margin={{
            top: 14,
            right: 30,
            left: 30,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='date' tick={{ fontSize: 12 }} tickFormatter={formatXAxis} />
          <YAxis
            label={{
              value: intl.formatMessage(messages.velocityChartYAxisLabel),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
            }}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {currentDayIndex > -1 && (
            <ReferenceLine
              label={{
                value: intl.formatMessage(messages.velocityChartTodayLabel),
                position: 'top',
                fill: '#666',
                fontSize: 12,
              }}
              stroke={REFERENCE_LINE_COLOR}
              strokeDasharray='3 3'
              x={data[currentDayIndex].date}
            />
          )}
          <Line
            activeDot={{ r: 5 }}
            dataKey='value'
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (payload.value === null || payload.value === undefined) {
                return null;
              }
              if (payload.isCurrentDay) {
                return (
                  <svg height={CURRENT_DAY_SIZE} width={CURRENT_DAY_SIZE} x={cx - CURRENT_DAY_SIZE / 2} y={cy - CURRENT_DAY_SIZE / 2}>
                    <circle cx={CURRENT_DAY_SIZE / 2} cy={CURRENT_DAY_SIZE / 2} fill={CURRENT_DAY_COLOR} r={CURRENT_DAY_SIZE / 2} />
                  </svg>
                );
              }
              return <circle cx={cx} cy={cy} fill={HISTORICAL_COLOR} r={3} />;
            }}
            name={intl.formatMessage(messages.velocityChartHistoricalLabel)}
            stroke={HISTORICAL_COLOR}
            strokeWidth={2}
            type='monotone'
          />
          <Line
            activeDot={{ r: 5 }}
            dataKey='forecastValue'
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (payload.forecastValue === null || payload.forecastValue === undefined) {
                return null;
              }
              if (payload.isCurrentDay) {
                return (
                  <svg height={CURRENT_DAY_SIZE} width={CURRENT_DAY_SIZE} x={cx - CURRENT_DAY_SIZE / 2} y={cy - CURRENT_DAY_SIZE / 2}>
                    <circle cx={CURRENT_DAY_SIZE / 2} cy={CURRENT_DAY_SIZE / 2} fill={CURRENT_DAY_COLOR} r={CURRENT_DAY_SIZE / 2} />
                  </svg>
                );
              }
              return <circle cx={cx} cy={cy} fill={FORECAST_COLOR} r={3} />;
            }}
            name={intl.formatMessage(messages.velocityChartForecastLabel)}
            stroke={FORECAST_COLOR}
            strokeDasharray='5 5'
            strokeWidth={2}
            type='monotone'
          />
          <Line
            activeDot={{ r: 5 }}
            dataKey='stockLevel'
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (payload.stockLevel === null || payload.stockLevel === undefined) {
                return null;
              }
              if (payload.isCurrentDay) {
                return (
                  <svg height={CURRENT_DAY_SIZE} width={CURRENT_DAY_SIZE} x={cx - CURRENT_DAY_SIZE / 2} y={cy - CURRENT_DAY_SIZE / 2}>
                    <circle cx={CURRENT_DAY_SIZE / 2} cy={CURRENT_DAY_SIZE / 2} fill={CURRENT_DAY_COLOR} r={CURRENT_DAY_SIZE / 2} />
                  </svg>
                );
              }
              return <circle cx={cx} cy={cy} fill={STOCK_LEVEL_COLOR} r={3} />;
            }}
            name={intl.formatMessage(messages.stockLevelLabel)}
            stroke={STOCK_LEVEL_COLOR}
            strokeWidth={2}
            type='monotone'
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VelocityChart;
